import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ApprovalIcon from "@mui/icons-material/Approval";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

// import { updateWorkedHours } from '../../service/WorkedHourService';
import ProcessStatus from "../constant_components/ProcessStatus";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import axiosClient from "@service/axiosClient";

const PlantHoursDG = ({ plant, periodStart }) => {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(true);

  //AG Grid
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [open, setOpen] = useState(true);
  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});
  // Prevent default browser context menu
  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  const initialRows = useMemo(() => {
    // Define the function inside useMemo for clarity
    const genRows = (plant) => {
      return plant.workers
        .filter((worker) => worker.timecards.length > 0)
        .flatMap((worker) =>
          worker.timecards.map((timecard) => ({
            id: worker.id,
            Cont_id: worker.contractor_id,
            Name: worker.name,
            Sun: timecard.worked_hours[0].hours_worked,
            Mon: timecard.worked_hours[1].hours_worked,
            Tue: timecard.worked_hours[2].hours_worked,
            Wed: timecard.worked_hours[3].hours_worked,
            Thur: timecard.worked_hours[4].hours_worked,
            Fri: timecard.worked_hours[5].hours_worked,
            Sat: timecard.worked_hours[6].hours_worked,
            Total: timecard.worked_hours.reduce(
              (total, hour) => total + parseFloat(hour.hours_worked),
              0
            ),
            Status: timecard.finalized
              ? "Finalized"
              : timecard.worked_hours.reduce(
                  (total, hour) => total + parseFloat(hour.hours_worked),
                  0
                ) <= 0
              ? timecard.id <= 0
                ? "Missing"
                : "Pending"
              : "Pending",
            workerId: worker.id,
            timecard_id: timecard.id,
            workedHoursIds: timecard.worked_hours.map((hour) => hour.id),
          }))
        );
    };
    // Call genRows with the current plant prop
    return genRows(plant);
  }, [plant]);

  useEffect(() => {
    setRows(initialRows);
    setIsLoading(false);
  }, [initialRows, periodStart]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleFinalize = (rowsApproved) => async () => {
    // console.log("rows approved", rowsApproved);
    const timeCardIds = rowsApproved.map((row) => row.timecard_id);
    if (!timeCardIds || timeCardIds.length === 0 || timeCardIds[0] <= 0) {
      triggerSnackbar(`hours not informed`);
      return;
    }

    const updatedRows = rows.map((row) => {
      if (timeCardIds.includes(row.timecard_id)) {
        return { ...row, Status: "Finalized" };
      }
      return row;
    });

    setRows(updatedRows);

    await axiosClient.post("/api/finalize-timecards", {
      timeCardIds: timeCardIds,
    });
  };

  // Actions cell renderer for AG Grid
  const ActionsCellRenderer = (props) => {
    const status = props.data.Status;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            handleFinalize([{ timecard_id: props.data.timecard_id }])()
          }
          sx={{
            color: status === "Pending" ? "primary.main" : "error.main",
          }}
        >
          <ApprovalIcon />
        </IconButton>
      </div>
    );
  };

  // Status cell renderer with colored cells
  const StatusCellRenderer = (props) => {
    const getStatusClass = (status) => {
      if (status === "Pending") return "tw-bg-yellow-100 tw-text-yellow-800";
      if (status === "Finalized") return "tw-bg-green-100 tw-text-green-800";
      if (status === "Missing") return "tw-bg-red-100 tw-text-red-800";
      return "";
    };

    return (
      <div
        className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${getStatusClass(
          props.value
        )}`}
      >
        {props.value}
      </div>
    );
  };

  const genColumns = (start) => {
    const startDate = start;
    const addDays = (date, days) => {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    };

    const weekDates = [...Array(7).keys()].map((i) => {
      const date = addDays(startDate, i);
      return `${date.toLocaleDateString("en", { weekday: "narrow" })} ${
        date.getMonth() + 1
      }-${date.getDate()}`;
    });

    // AG Grid column definitions
    const columns = [
      {
        field: "Cont_id",
        headerName: "ID",
        width: 90,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      { field: "Name", headerName: "Name", width: 210 },
      {
        field: "Sun",
        headerName: weekDates[0],
        width: 75,
        editable: false,
      },
      {
        field: "Mon",
        headerName: weekDates[1],
        width: 75,
        editable: false,
      },
      {
        field: "Tue",
        headerName: weekDates[2],
        width: 75,
        editable: false,
      },
      {
        field: "Wed",
        headerName: weekDates[3],
        width: 75,
        editable: false,
      },
      {
        field: "Thur",
        headerName: weekDates[4],
        width: 75,
        editable: false,
      },
      {
        field: "Fri",
        headerName: weekDates[5],
        width: 75,
        editable: false,
      },
      {
        field: "Sat",
        headerName: weekDates[6],
        width: 75,
        editable: false,
      },
      { field: "Total", headerName: "Total", width: isMobile ? 60 : 60 },
      {
        field: "actions",
        headerName: "Actions",
        width: 75,
        cellRenderer: ActionsCellRenderer,
        sortable: false,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 75,
        cellRenderer: StatusCellRenderer,
      },
    ];
    return columns;
  };

  const calculateTotalHours = (rows) => {
    return rows.reduce((sum, row) => sum + parseFloat(row.Total || 0), 0);
  };

  const totalHours = useMemo(() => calculateTotalHours(rows), [rows]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    setSelectedRows(selectedData);
  };

  const CustomFooter = ({ totalHours }) => (
    <Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <ProcessStatus
            lst={rows.map((row) => {
              return { Status: row.Status };
            })}
            process="Plant Hours"
          />
        </Box>
        <Typography variant="subtitle1">Hours Total: {totalHours}</Typography>
        <Button sx={{ color: "#2642c9" }} onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}{" "}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        marginBottom: 3,
      }}
    >
      {isLoading ? (
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          {[...new Array(5)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={60}
              sx={{ bgcolor: "grey.300", mb: 1 }}
            />
          ))}
        </Box>
      ) : (
        <Collapse in={open}>
          <Box sx={{ width: "100%" }}>
            <Box
              className="ag-theme-balham"
              sx={{
                width: "100%",
                maxHeight: 500,
                overflow: "auto",
              }}
              onContextMenu={handleContextMenu}
            >
              <AgGridReact
                rowData={rows}
                columnDefs={genColumns(periodStart)}
                defaultColDef={defaultColDef}
                getRowId={(params) => `${params.data.workerId}`}
                rowSelection="multiple"
                onSelectionChanged={onSelectionChanged}
                onCellContextMenu={handleCellContextMenu}
                animateRows={true}
                pagination={false}
                domLayout="autoHeight"
              />
              {ContextMenuComponent}
            </Box>
            <Box
              sx={{
                borderLeft: 1,
                borderRight: 1,
                borderBottom: 1,
                borderColor: "divider",
                p: 1,
                bgcolor: "background.paper",
              }}
            >
              <Button
                color="primary"
                sx={{ bgcolor: "#2642c9", textTransform: "none" }}
                variant="contained"
                startIcon={<ApprovalIcon />}
                onClick={handleFinalize(selectedRows)}
              >
                Finalize Selected
              </Button>
            </Box>
          </Box>
        </Collapse>
      )}
      <CustomFooter totalHours={totalHours} />
    </Box>
  );
};

export default PlantHoursDG;
